import {cardSetSize} from '../cells/card-set-size'
import {cellHasUnloadedItems} from '../cells/cell-has-unloaded-items'
import {composeCellKey} from '../provider/cell-key'
import type {BoardConfig, BoardDataContextValue} from '../provider/contexts'
import type {BoardCell} from '../types'
import type {BoardSelectedCard} from './types'

/**
 * One card, in the words a reader knows rather than the keys the board indexes by, plus
 * the caller's own item.
 *
 * Every field but `card` is optional, and absent always means absent rather than
 * unknown-so-here-is-something-else. A selected card outlives its item: the cell it is in
 * can page away, or somebody else can move the item out of it, and a column can leave a
 * board whose axis pages. Answering such a card with its raw id would put an opaque
 * identity in front of a reader while claiming it was a name, which is the same failure
 * as naming a cell after a count nobody stated.
 *
 * The item is looked up against the caller's current data every time, never snapshotted
 * when the card was selected, so a detail is never a stale copy of something the caller
 * has since changed.
 */
export type BoardCardDetail<TItem> = {
  /**
   * The identity that was passed in, handed straight back, so a mapped list of these is
   * self-contained and can be keyed with `boardSelectionKey`.
   */
  card: BoardSelectedCard
  /** The column's stated label, or absent when the column is not in the caller's data. */
  columnLabel?: string
  /**
   * The row's stated label, or absent when there is no row half to name.
   *
   * Absent for the same two reasons a cell's name has no row half: the row is not in the
   * caller's data, or the board has no row headers, so its single row means nothing to a
   * reader and putting its label in front of one would show a name nobody can see.
   */
  rowLabel?: string
  /**
   * What the board itself calls this cell: `labels.cell` with the same parts, and the
   * same stated count, that {@link useBoardCell} gives it.
   *
   * Here so a caller drawing a selection and the board announcing a cell cannot spell the
   * same place two ways on one page. Absent unless both halves of the address resolve,
   * which is the rule `useBoardCell` follows before it names a cell at all: an address
   * missing either half is not a place on this board, and naming it after the half that
   * did resolve would put a name on somewhere the board draws nothing for.
   */
  cellLabel?: string
  /**
   * The card's own name, from the caller's `getItemLabel`, or absent when the item is.
   *
   * The same accessor the caller already states to the provider, which is what makes this
   * a fact read back rather than one restated.
   */
  itemLabel?: string
  /** The caller's item, or absent when the caller's data no longer holds it. */
  item?: TItem
}

/**
 * Resolves one card. The type parameter is taken here rather than on the hook, exactly as
 * `useBoardData<TItem>()` takes it, so nothing else in the selection surface goes generic
 * over the caller's item type.
 */
export type BoardCardDescriber = <TItem>(card: BoardSelectedCard) => BoardCardDetail<TItem>

/**
 * Builds the resolver behind `useBoardSelection().describeCard`, over one `data`, one
 * config and the item index that outlives both.
 *
 * **Cost.** A column, a row and a cell are each a lookup in a `Map` the provider already
 * built, so those are O(1) from the first card onwards. The item is not: the first card
 * resolved in a cell builds the provider's cell item index for it, which is linear in
 * that cell's **loaded** cards, and every card resolved in that cell afterwards is O(1).
 * A selection never leaves one cell, so a whole selection of any size builds at most one
 * index, and only its first card pays for it.
 *
 * Nothing is proportional to the number of columns, rows or cells on the board, or to the
 * cards in any cell but the one being resolved.
 */
export function createCardDescriber({
  data,
  config,
}: {
  data: BoardDataContextValue
  config: BoardConfig
}): BoardCardDescriber {
  const {columnsByKey, rowsByKey, cellsByAddress, itemIndex} = data
  const {getItemId, getItemLabel, labels, hasRowHeaders} = config

  return function describeCard<TItem>(card: BoardSelectedCard): BoardCardDetail<TItem> {
    const cellKey = composeCellKey(card)
    const cell = cellsByAddress.get(cellKey)
    const item = itemIndex.itemIn(cellKey, card.itemId, getItemId)

    const column = columnsByKey.get(card.columnKey)
    const row = rowsByKey.get(card.rowKey)

    return {
      card,
      columnLabel: column?.label,
      // The row half only on a board that names its rows, which is the rule
      // `useBoardCell` applies to the same label for the same reason. Whether the row
      // resolves at all is a different question, and it is asked below.
      rowLabel: hasRowHeaders ? row?.label : undefined,
      // Both halves have to resolve before this is a cell at all, exactly as in
      // `useBoardCell`, which gives an address missing either one no role and no name.
      // Naming it after the half that did resolve would hand a caller a name for a place
      // the board draws nothing for.
      cellLabel:
        column === undefined || row === undefined
          ? undefined
          : labels.cell({
              columnLabel: column.label,
              rowLabel: hasRowHeaders ? row.label : undefined,
              totalCount: statedSetSize(cell),
            }),
      itemLabel: item === undefined ? undefined : getItemLabel(item),
      item: item as TItem | undefined,
    }
  }
}

/**
 * The count a cell's name states, or absent when its caller stated none.
 *
 * The same two rules `useBoardCell` follows: only a stated count is a fact, and a stated
 * one is floored at the cards already loaded.
 */
function statedSetSize(cell: BoardCell<unknown> | undefined): number | undefined {
  if (cell?.totalCount === undefined) return undefined

  return cardSetSize({totalCount: cell.totalCount, hasMoreItems: cellHasUnloadedItems(cell), loaded: cell.items.length})
}
